import request from "./client";

export const getById = async ({ id }) => {
  const resp = await request.get(`/v1/delivery_notes/${id}`);
  return { data: resp.body };
};
export const getSome = async ({
  q = "",
  filter = "",
  limit = 10,
  offset = 0,
  sort_by = "number",
  sort_dir = "desc",
}) => {
  const query = new URLSearchParams({
    limit,
    offset,
    ...(filter && { filter }),
    ...(q && { q }),
    sort_by,
    sort_dir,
  }).toString();
  try {
    const resp = await request.get(`/v1/delivery_notes?${query}`);
    const body = resp.body;
    return { total: body.meta.total, data: body.results, error: null };
  } catch (e) {
    if (!e.response) {
      return { total: 0, data: [], error: e };
    }
    return { total: 0, data: [], error: e.response.body };
  }
};

export const getNeighbors = async ({
  id,
  q = "",
  filter = "",
  sort_by = "number",
  sort_dir = "desc",
}) => {
  const query = new URLSearchParams({
    ...(filter && { filter }),
    ...(q && { q }),
    sort_by,
    sort_dir,
  }).toString();
  try {
    const resp = await request.get(`/v1/delivery_notes/${id}/neighbors?${query}`);
    return { data: resp.body, error: null };
  } catch (e) {
    if (!e.response) {
      return { data: null, error: e };
    }
    return { data: null, error: e.response };
  }
};

export const createOne = async ({
  state,
  template,
  customer,
  customer_property,
  items,
  uploads,
  comments,
  documentation_schema,
  documentation,
  remark,
  customer_signature,
  customer_signature_name,
  customer_signature_date,
  employee_signature,
  employee_signature_name,
  coworker_ids,
  performed_by_id,
  work_order_id,
}) => {
  try {
    const resp = await request.post(`/v1/delivery_notes`).send({
      state: state,
      template: template,
      customer: customer,
      customer_property: customer_property,
      items: items,
      uploads: uploads,
      comments: comments,
      documentation_schema: documentation_schema,
      documentation: documentation,
      remark: remark,
      customer_signature: customer_signature,
      customer_signature_name: customer_signature_name,
      customer_signature_date: customer_signature_date,
      employee_signature: employee_signature,
      employee_signature_name: employee_signature_name,
      coworker_ids: coworker_ids || [],
      performed_by_id: performed_by_id ?? null,
      work_order_id: work_order_id ?? null,
    });
    const body = resp.body;
    return { data: body, error: null };
  } catch (e) {
    if (!e.response) {
      return { data: null, error: e };
    }
    return { data: null, error: e.response };
  }
};

export const updateOne = async ({
  id,
  state,
  version,
  template,
  customer,
  customer_property,
  items,
  uploads,
  comments,
  documentation_schema,
  documentation,
  remark,
  customer_signature,
  customer_signature_name,
  customer_signature_date,
  employee_signature,
  employee_signature_name,
  coworker_ids,
  performed_by_id,
}) => {
  try {
    const resp = await request.put(`/v1/delivery_notes/${id}`).send({
      state,
      version,
      template,
      customer,
      customer_property,
      items,
      uploads,
      comments,
      documentation_schema,
      documentation,
      remark,
      customer_signature,
      customer_signature_name,
      customer_signature_date,
      employee_signature,
      employee_signature_name,
      coworker_ids: coworker_ids || [],
      performed_by_id: performed_by_id ?? null,
    });
    const body = resp.body;
    return { data: body, error: null };
  } catch (e) {
    if (!e.response) {
      return { data: null, error: e };
    }
    return { data: null, error: e.response };
  }
};

export const generatePdf = async ({ id }) => {
  try {
    const resp = await request
      .post(`/v1/delivery_notes/${id}/pdf`)
      .responseType("blob");
    return { data: resp.body, error: null };
  } catch (e) {
    if (!e.response) {
      return { data: null, error: e };
    }
    return { data: null, error: e.response.body };
  }
};

export const addComment = async ({ id, text }) => {
  try {
    const resp = await request
      .post(`/v1/delivery_notes/${id}/comments`)
      .send({ text });
    return { data: resp.body, error: null };
  } catch (e) {
    if (!e.response) {
      return { data: null, error: e };
    }
    return { data: null, error: e.response.body };
  }
};

export const updateComment = async ({ id, commentId, text }) => {
  try {
    const resp = await request
      .put(`/v1/delivery_notes/${id}/comments/${commentId}`)
      .send({ text });
    return { data: resp.body, error: null };
  } catch (e) {
    if (!e.response) {
      return { data: null, error: e };
    }
    return { data: null, error: e.response.body };
  }
};

export const deleteComment = async ({ id, commentId }) => {
  try {
    await request.delete(`/v1/delivery_notes/${id}/comments/${commentId}`);
    return { error: null };
  } catch (e) {
    if (!e.response) {
      return { error: e };
    }
    return { error: e.response.body };
  }
};

export const deleteFile = async ({ id, fileId }) => {
  try {
    await request.delete(`/v1/delivery_notes/${id}/files/${fileId}`);
    return { error: null };
  } catch (e) {
    if (!e.response) {
      return { error: e };
    }
    return { error: e.response.body };
  }
};

export const assignDeliveryNote = async ({ id, assigned_to_id }) => {
  try {
    const resp = await request
      .patch(`/v1/delivery_notes/${id}/assign`)
      .send({ assigned_to_id });
    return { data: resp.body, error: null };
  } catch (e) {
    if (!e.response) {
      return { data: null, error: e };
    }
    return { data: null, error: e.response };
  }
};

export const getDeliveryNoteItems = async ({
  templateId,
  createdBy,
  sortBy,
  lastNotes,
  limit,
}) => {
  const query = new URLSearchParams({
    ...(templateId && { template_id: templateId }),
    ...(createdBy && { created_by: createdBy }),
    ...(sortBy && { sort_by: sortBy }),
    ...(lastNotes && { last_notes: lastNotes }),
    ...(limit && { limit }),
  }).toString();
  try {
    const resp = await request.get(`/v1/delivery_note_items?${query}`);
    return { data: resp.body.results, error: null };
  } catch (e) {
    if (!e.response) {
      return { data: [], error: e };
    }
    return { data: [], error: e.response.body };
  }
};

export const getShareLink = async ({ id }) => {
  try {
    const resp = await request.post(`/v1/delivery_notes/${id}/share_link`);
    return { data: resp.body, error: null };
  } catch (e) {
    if (!e.response) {
      return { data: null, error: e };
    }
    return { data: null, error: e.response.body };
  }
};

export const sendEmail = async ({
  id,
  to,
  subject,
  message,
  upload_ids = [],
}) => {
  try {
    const resp = await request
      .post(`/v1/delivery_notes/${id}/email`)
      .send({ to, subject, message, upload_ids });
    return { data: resp.body, error: null };
  } catch (e) {
    if (!e.response) {
      return { data: null, error: e };
    }
    return { data: null, error: e.response.body };
  }
};

export const changeState = async ({ id, state, attrs }) => {
  try {
    const resp = await request
      .post(`/v1/delivery_notes/${id}/state_transitions`)
      .send({
        state,
        attrs,
      });
    const body = resp.body;
    return { data: body, error: null };
  } catch (e) {
    if (!e.response) {
      return { data: null, error: e };
    }
    return { data: null, error: e.response };
  }
};
